'use client';

import { ReactNode, useCallback, useEffect, useRef } from 'react';
import config from 'config';
import { withPublicRouting } from 'hocs';

import {
  Box,
  Flex,
  Stack,
  useBreakpointValue,
  useTheme
} from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import { scrollToElement } from 'utils';

import { Carousel } from 'ui';

const {
  login: { desktop_banners, mobile_banners }
} = config;

const AuthLayout = ({ children }: { children: ReactNode }) => {
  const {
    colors: {
      palette: {
        auth: { layout }
      }
    }
  } = useTheme();
  const { redirectFlow } = useAppSelector((state) => state.user);

  const isTablet = useBreakpointValue(
    { base: true, lg: false },
    { fallback: 'lg' }
  );
  const anchorRef = useRef(null);

  const scrollIntoView = useCallback(() => {
    if (redirectFlow !== null) {
      scrollToElement(anchorRef, 'smooth');
    }
  }, [redirectFlow]);

  useEffect(() => {
    setTimeout(() => {
      scrollIntoView();
    }, 0);
  }, [scrollIntoView]);

  return (
    <Box
      ref={anchorRef}
      flex={1}
      position='relative'
      w='100%'
      maxW='container.xl'
      display='flex'
      flexDir='column'
      justifyContent='flex-start'
      _after={{
        content: '""',
        display: 'block',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100vw',
        height: '100%',
        bg: layout.afterBg,
        zIndex: -1
      }}
    >
      <Stack
        direction={{ base: 'column-reverse', lg: 'row' }}
        alignItems='stretch'
        justifyContent='center'
        overflow='hidden'
        gap={{ base: '10px', sm: '15px', md: '25px', xl: '38px' }}
        h='100%'
        mb={{ base: 'auto', lg: 0 }}
        px={{ base: '15px', md: '25px' }}
        pt={{ base: '10px', md: '20px', xl: '32px' }}
        pb={{ base: '40px', md: '30px', xl: '32px' }}
      >
        <Box w='100%' maxW='820px'>
          <Flex
            alignItems='flex-start'
            justifyContent='center'
            height='100%'
            w='100%'
            bg={layout.formBg}
            boxShadow={`0 1px 6px ${layout.boxShadow}`}
            px={{ base: '27px', md: '25px', xl: '84px' }}
            pt={{ base: '35px', md: '45px', xl: '50px' }}
            pb={{ base: '35px', md: '45px', xl: '30px' }}
            borderRadius='5px'
          >
            {children}
          </Flex>
        </Box>

        {isTablet
          ? mobile_banners.length > 0 && (
              <Box mt={2}>
                <Carousel
                  banners={mobile_banners}
                  settings={{ fade: true, speed: 1000 }}
                  borderRadius={5}
                />
              </Box>
            )
          : desktop_banners.length > 0 && (
              <Flex
                w='100%'
                flexDirection='column'
                maxW={{ base: '420px', xl: '480px' }}
              >
                <Carousel
                  banners={desktop_banners}
                  settings={{ fade: true, speed: 1000 }}
                  borderRadius={5}
                />
              </Flex>
            )}
      </Stack>
    </Box>
  );
};

export default withPublicRouting(AuthLayout);
